import React, { useEffect, useRef, useState, memo, useCallback, useMemo } from "react";
import fusoLogo from "../../assets/fuso-logo.svg";
import feedPnc from "../../assets/feedStarPnc.svg";
import historyClosePnc from "../../assets/toggle-button.svg";
import toggleBtn from "../../assets/historyToggleMobile.svg";
import ReactDOM from "react-dom";
import logoutPnc from "../../assets/logoutPnc.svg";
import aboutSide from "../../assets/aboutSidebarPnc.svg";
import "./Sidebar.css";
import ProfileSection from "../ProfileSection/ProfileSection.js";
import PinnedChats from "../PinnedChats/PinnedChats.js";
import HistorySection from "../HistorySection/HistorySection.js";
import ActionButtons from "../ActionButtons/ActionButtons.js";
import MobileFeedbackModal from "../MobileFeedbackModal/MobileFeedbackModal.js";
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";
import { useTranslation } from 'react-i18next';


// Move date utilities outside component to prevent recreation
const dateUtils = {
  isToday: (date) => {
    const today = new Date();
    return dateUtils.isSameUTCDay(date, today);
  },

  isYesterday: (date) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return dateUtils.isSameUTCDay(date, yesterday);
  },

  isSameUTCDay: (date1, date2) => {
    const d1 = dateUtils.getUTCDateParts(date1);
    const d2 = dateUtils.getUTCDateParts(date2);
    return d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
  },

  getUTCDateParts: (date) => {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
    };
  },

  isWithinLast7Days: (date) => {
    const now = new Date();
    for (let i = 2; i <= 7; i++) {
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - i);
      if (dateUtils.isSameUTCDay(date, pastDate)) {
        return true;
      }
    }
    return false;
  },

  isWithinLast30Days: (date) => {
    const now = new Date();
    for (let i = 8; i <= 30; i++) {
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - i);
      if (dateUtils.isSameUTCDay(date, pastDate)) {
        return true;
      }
    }
    return false;
  },

  isOlderThan30Days: (date) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    return date < thirtyDaysAgo;
  },
};

// Improved memoized components with better comparison
const MemoizedHistorySection = memo(HistorySection, (prevProps, nextProps) => {
  return (
    prevProps.selectedHistoryId === nextProps.selectedHistoryId &&
    prevProps.items.length === nextProps.items.length &&
    prevProps.activeDropdownId === nextProps.activeDropdownId &&
    prevProps.items.every((item, index) => 
      item.id === nextProps.items[index]?.id && 
      item.title === nextProps.items[index]?.title
    )
  );
});

const MemoizedPinnedChats = memo(PinnedChats, (prevProps, nextProps) => {
  return (
    prevProps.selectedHistoryId === nextProps.selectedHistoryId &&
    prevProps.pinnedChats.length === nextProps.pinnedChats.length &&
    prevProps.activeDropdownId === nextProps.activeDropdownId &&
    prevProps.pinnedChats.every((item, index) => 
      item.id === nextProps.pinnedChats[index]?.id && 
      item.title === nextProps.pinnedChats[index]?.title
    )
  );
});

const Sidebar = ({
  isSidebarOpen,
  toggleSidebar,
  resetToWelcomePage,
  toggleFeedbackModal,
  chatHistory,
  pinnedChats,
  selectedHistoryId,
  handleHistoryItemClick,
  isLoadingHistory,
  historyError,
  toggleDropdown,
  activeDropdownId,
  handleDeleteButtonClick,
  handlePinChat,
  handleUnpinButtonClick,
  toggleAboutModal,
  handleLogout,
  userId,
}) => {
  const { t } = useTranslation();
  
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileFeedbackModal, setShowMobileFeedbackModal] = useState(false);
  const [rating, setRating] = useState(4);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    rating: false,
    feedback: false,
  });
  const [longPressTimer, setLongPressTimer] = useState(null);

  // Memoize filter functions to prevent unnecessary recalculations
  const historyFilters = useMemo(() => ({
    getTodayHistory: () => {
      return chatHistory.filter(
        (item) => dateUtils.isToday(item.timestamp) && item.pinned !== 1
      );
    },
    getYesterdayHistory: () => {
      return chatHistory.filter(
        (item) => dateUtils.isYesterday(item.timestamp) && item.pinned !== 1
      );
    },
    getLast7DaysHistory: () => {
      return chatHistory.filter(
        (item) => dateUtils.isWithinLast7Days(item.timestamp) && item.pinned !== 1
      );
    },
    getLast30DaysHistory: () => {
      return chatHistory.filter(
        (item) => dateUtils.isWithinLast30Days(item.timestamp) && item.pinned !== 1
      );
    },

    getOlderHistory: () => {
    return chatHistory.filter(
      (item) => dateUtils.isOlderThan30Days(item.timestamp) && item.pinned !== 1
    );
  },
  }), [chatHistory]);

  // Stable callback functions using useCallback
  const handleLongPressStart = useCallback((itemId, e) => {
    if (window.innerWidth >= 640) return; 
    if (longPressTimer) clearTimeout(longPressTimer);
    const timer = setTimeout(() => {
      toggleDropdown(itemId, e);
    }, 500);
    setLongPressTimer(timer);
  }, [longPressTimer, toggleDropdown]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  }, [longPressTimer]);

  const handleHistoryItemClickWithSidebarToggle = useCallback((item) => {
    handleHistoryItemClick(item);
    if (isSmallScreen) {
      toggleSidebar();
    }
  }, [handleHistoryItemClick, isSmallScreen, toggleSidebar]);

  // Screen size detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsSmallScreen(window.innerWidth < 992);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Click outside sidebar handler for mobile
  useEffect(() => {
    function handleClickOutsideSidebar(event) {
      if (!isMobile || !isSidebarOpen) return;
      
      const sidebarElement = document.querySelector(".sidebarPnc");
      if (sidebarElement && !sidebarElement.contains(event.target)) {
        toggleSidebar();
      }
    }

    document.addEventListener("mousedown", handleClickOutsideSidebar);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSidebar);
    };
  }, [isSidebarOpen, isMobile, toggleSidebar]);

  // Cleanup long press timer
  useEffect(() => {
    return () => {
      if (longPressTimer) clearTimeout(longPressTimer);
    };
  }, [longPressTimer]);

  // Click outside dropdown handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown(null, event);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [toggleDropdown]);

  // Profile menu handlers
  const handleProfileClick = useCallback(() => {
    setShowProfileMenu(true);
  }, []);

  const handleCloseProfileMenu = useCallback(() => {
    setShowProfileMenu(false);
  }, []);

  const handleLogoutClick = useCallback(() => {
    handleLogout();
    setShowProfileMenu(false);
  }, [handleLogout]);

  const handleAboutChatbot = useCallback(() => {
    toggleAboutModal();
    setShowProfileMenu(false);
  }, [toggleAboutModal]);

  // Feedback handlers
  const handleFeedbackButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMobile) {
      setShowMobileFeedbackModal(true);
    } else {
      toggleFeedbackModal();
    }
  }, [isMobile, toggleFeedbackModal]);

  const handleCloseMobileFeedbackModal = useCallback(() => {
    setShowMobileFeedbackModal(false);
    setRating(4);
    setFeedback("");
    setValidationErrors({
      rating: false,
      feedback: false,
    });
    setSubmitError(null);
  }, []);

  const validateForm = useCallback(() => {
    const errors = {
      rating: rating === 0,
      feedback: feedback.trim() === "",
    };

    setValidationErrors(errors);
    return !errors.rating && !errors.feedback;
  }, [rating, feedback]);

  const handleSubmitFeedback = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_ADD_FEEDBACK, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: sessionStorage.getItem('user_id') || localStorage.getItem('user_id') || userId || "",
          rating: rating,
          feedback: feedback,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      setSubmitSuccess(true);
      setShowSuccessToast(true);
      
      setTimeout(() => {
        setRating(4);
        setFeedback("");
        setSubmitSuccess(false);
        setShowMobileFeedbackModal(false);
        setTimeout(() => {
          setShowSuccessToast(false);
        }, 2000);
      }, 1500);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, rating, feedback, userId]);

  // Success toast component
  const SuccessToast = () => showSuccessToast && ReactDOM.createPortal(
    <div
      className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center feedsubmitPnc"
      style={{ left: "auto", right: "1rem" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 mr-2"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
        {t('feedbackSubmittedSuccess')}
    </div>,
    document.body
  );

  // History sections data - FIXED: Using translation function instead of hardcoded strings
  const historySections = useMemo(() => [
    { title: t('Today'), items: historyFilters.getTodayHistory() },
    { title: t('Yesterday'), items: historyFilters.getYesterdayHistory() },
    { title: t('Past 7 Days'), items: historyFilters.getLast7DaysHistory() },
    { title: t('previous30Days'), items: historyFilters.getLast30DaysHistory() },
    { title: t('Older'), items: historyFilters.getOlderHistory() },
  ], [historyFilters, t]);

  if (isMobile) {
    return (
      <>
        <SuccessToast />
        
        <aside
          className={`w-60 border-r border-gray-200 flex-shrink-0 transition-transform duration-300 ease-in-out sidebarPnc ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col">
            <div className="flex items-center p-4 border-gray-200 historyIconPnc">
              <button
                type="button"
                className="border-none p-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSidebar();
                }}
              >
                <img src={toggleBtn} alt="Toggle Button" />
              </button>
            </div>

            <div className="flex-grow">
              <h2 className="conversationHeadingPnc">{t('conversationHistory')}</h2>

              {historyError && (
                <div className="text-center py-2">
                  <p className="text-red-500">{historyError}</p>
                </div>
              )}

              {!isLoadingHistory && !historyError && (
                <div className="space-y-4 historyTabPnc">
                  {/* Pinned Chats Section */}
                  {pinnedChats.length > 0 && (
                    <MemoizedPinnedChats
                      pinnedChats={pinnedChats}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={handleHistoryItemClickWithSidebarToggle}
                      handleLongPressStart={handleLongPressStart}
                      handleLongPressEnd={handleLongPressEnd}
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handleUnpinButtonClick={handleUnpinButtonClick}
                      isMobile={isMobile}
                    />
                  )}

                  {/* History Sections */}
                  {historySections.map(({ title, items }) => (
                    <MemoizedHistorySection
                      key={title}
                      title={title}
                      items={items}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={handleHistoryItemClickWithSidebarToggle}
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handlePinChat={handlePinChat}
                    />
                  ))}

                  {/* No history message */}
                  {chatHistory.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-gray-500 text-left">
                        No conversation history available
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed bottom section */}
            <div className="mt-auto flex flex-col p-3 sidebarBtnNew">
              <ActionButtons
                resetToWelcomePage={() => {
                  resetToWelcomePage();
                  if (window.innerWidth < 640) {
                    toggleSidebar();
                  }
                }}
                handleFeedbackButtonClick={handleFeedbackButtonClick}
                isMobile={isMobile}
              />
              <ProfileSection handleProfileClick={handleProfileClick} />
            </div>
          </div>
        </aside>

        {/* Profile Menu - Mobile view */}
        {showProfileMenu && ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
            onClick={handleCloseProfileMenu}
          >
            <div
              className="bg-white w-full profileSmallOuterPnc"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-rose-50 p-4 border-b border-gray-200 profileSmallPnc slideupModal">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium profileModalPnc">Profile</h2>
                  <button
                    onClick={handleCloseProfileMenu}
                    className="text-2xl text-gray-500 crossFeedBtnPnc"
                  >
                    <svg
                      className="svgFeedPnc"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="py-2">
                <button
                  onClick={handleAboutChatbot}
                  className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-100 border-none"
                >
                  <span className="inline-block mr-3">
                    <img src={aboutSide} alt="About" className="w-6 h-6" />
                  </span>
                  <span>About Chatbot</span>
                </button>

                <button
                  onClick={handleLogoutClick}
                  className="w-full text-left px-4 py-3 flex items-center text-red-600 hover:bg-gray-100 border-none"
                >
                  <span className="inline-block mr-3">
                    <img src={logoutPnc} alt="Logout" className="w-6 h-6" />
                  </span>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Mobile Feedback Modal */}
        {showMobileFeedbackModal && (
          <MobileFeedbackModal
            rating={rating}
            setRating={setRating}
            feedback={feedback}
            setFeedback={setFeedback}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            isSubmitting={isSubmitting}
            submitError={submitError}
            handleSubmitFeedback={handleSubmitFeedback}
            handleCloseMobileFeedbackModal={handleCloseMobileFeedbackModal}
          />
        )}
      </>
    );
  }

  // Desktop view
  return (
    <>
      <aside
        className={`w-72 bg-rose-50 border-r border-gray-200 flex-shrink-0 z-50 transition-transform duration-300 ease-in-out sidebarPnc ${
          isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed lg:hidden"
        }`}
      >
        <div className="sidebarExpand">
          <div className="p-4 historyIconPnc">
            <div className="flex items-center space-x-2">
              <img src={fusoLogo} alt="FUSO" className="h-8" />
            </div>
            <div className="flex items-center">
              <img
                src={historyClosePnc}
                alt="Toggle button"
                className="h-8 cursor-pointer"
                onClick={toggleSidebar}
              />
            </div>
          </div>
          
          <div className="sideButtonPnc">
            <ActionButtons
              resetToWelcomePage={resetToWelcomePage}
              handleFeedbackButtonClick={toggleFeedbackModal}
              isMobile={false}
            />
            
            <div className="space-y-4">
              <h2 className="conversationHeadingPnc">{t('conversationHistory')}</h2>

              {historyError && (
                <div className="text-center py-2">
                  <p className="text-red-500">{historyError}</p>
                </div>
              )}

              {!isLoadingHistory && !historyError && (
                <div className="space-y-4 historyTabPnc">
                  {/* Pinned Chats Section */}
                  {pinnedChats.length > 0 && (
                    <PinnedChats
                      pinnedChats={pinnedChats}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={handleHistoryItemClickWithSidebarToggle}
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handleUnpinButtonClick={handleUnpinButtonClick}
                      isMobile={isMobile}
                    />
                  )}

                  {/* History Sections */}
                  {historySections.map(({ title, items }) => (
                    <MemoizedHistorySection
                      key={title}
                      title={title}
                      items={items}
                      selectedHistoryId={selectedHistoryId}
                      handleHistoryItemClick={handleHistoryItemClickWithSidebarToggle}
                      toggleDropdown={toggleDropdown}
                      activeDropdownId={activeDropdownId}
                      dropdownRef={dropdownRef}
                      handleDeleteButtonClick={handleDeleteButtonClick}
                      handlePinChat={handlePinChat}
                    />
                  ))}

                  {/* No history message */}
                  {chatHistory.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-gray-500 text-left">
                        No conversation history available
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="feedSectionPnc">
              <div
                className="feedbackPnc cursor-pointer"
                onClick={toggleFeedbackModal}
              >
                <img src={feedPnc} alt="Give Feedback icon" />
                 {t('giveFeedback')}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <SuccessToast />
    </>
  );
};

export default Sidebar;